import { isInFlightStage } from 'components/CallScribe/postCallReviewPhase';
import {
  CallScribeCallDirection,
  CallScribeCallStatus,
  CallScribePipelineStage,
  ICallScribeCallListItem,
} from 'services/callScribe/types';

/**
 * iPhone-style display helpers for the Pod CallScribe Recents list. These are
 * intentionally pure / synchronous so the list can re-derive on every render
 * without paying for a refetch.
 */

/** US-style display formatter with a graceful E.164 / international fallback. */
export const formatPhoneE164ToDisplay = (raw: string | null | undefined): string => {
  if (!raw) {
    return '';
  }

  const digits = raw.replace(/\D+/g, '');
  // 1+10 (US/Canada) — render as (###) ###-####.
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  // 10 — assume US/Canada without country code.
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // International — keep the leading + when present, else add it.
  if (digits.length > 10) {
    return raw.startsWith('+') ? raw : `+${digits}`;
  }
  return raw;
};

/** Reduce a phone number to a digits-only key for grouping/cache lookups. */
export type PipelineRowState =
  | 'inFlight'
  | 'failed'
  | 'summaryReady'
  | 'nothingToSummarise'
  | 'noSummary';

export const resolvePipelineRowState = (
  stage: CallScribePipelineStage | null | undefined,
): PipelineRowState => {
  if (stage === CallScribePipelineStage.Failed) {
    return 'failed';
  }
  if (stage === CallScribePipelineStage.Completed) {
    return 'summaryReady';
  }
  if (stage === CallScribePipelineStage.NotApplicable) {
    return 'nothingToSummarise';
  }
  return isInFlightStage(stage) ? 'inFlight' : 'noSummary';
};

export const normalizePhoneKey = (raw: string | null | undefined): string => {
  if (!raw) {
    return '';
  }
  return raw.replace(/\D+/g, '');
};

/** Picks the "other party" phone for a call — the side that isn't the technician. */
export const otherPartyPhone = (call: ICallScribeCallListItem): string | null => {
  return call.direction === CallScribeCallDirection.Outbound ? call.toNumber : call.fromNumber;
};

const UNANSWERED_STATUSES = new Set<CallScribeCallStatus>([
  CallScribeCallStatus.NoAnswer,
  CallScribeCallStatus.Busy,
  CallScribeCallStatus.Failed,
  CallScribeCallStatus.Cancelled,
]);

export const isUnansweredCall = (call: ICallScribeCallListItem): boolean =>
  UNANSWERED_STATUSES.has(call.status);

/** Two uppercase initials from a contact display name. Empty string when not derivable. */
const NAME_SUFFIXES = new Set([
  'jr', 'jr.', 'sr', 'sr.', 'ii', 'iii', 'iv', 'v',
  'md', 'm.d.', 'phd', 'ph.d.', 'mba', 'cpa', 'esq', 'esq.', 'rn', 'dds',
]);

export const initialsFromName = (name: string | null | undefined): string => {
  if (!name) {
    return '';
  }
  const [beforeComma] = name.split(',');
  const parts = (beforeComma ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter(part => !NAME_SUFFIXES.has(part.toLowerCase()));
  if (parts.length === 0) {
    return '';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Parses a backend timestamp defensively: a date-time WITHOUT a timezone
 * designator is a UTC instant that JavaScript would otherwise read as local
 * time (shifting every call hours into the future), so we append `Z` first.
 */
export const parseUtcInstant = (iso: string | null | undefined): Date | null => {
  if (!iso) {
    return null;
  }
  const hasZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(iso);
  const needsZ = !hasZone && iso.includes('T');
  const d = new Date(needsZ ? `${iso}Z` : iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Exact local date-time for hover titles, e.g. "Aug 10, 2026, 9:14 AM". */
export const exactLocalLabel = (iso: string | null | undefined): string => {
  const d = parseUtcInstant(iso);
  if (!d) {
    return '';
  }
  return d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};

/**
 * Relative timestamp:
 *   - <60s  → "Just now"
 *   - <60m  → "5 min ago"
 *   - <24h  → "2 hr ago"
 *   - yesterday → "Yesterday"
 *   - <7d   → "Tuesday"
 *   - else  → "10/14/2024"
 */
export const relativeTimeLabel = (iso: string | null | undefined, now: Date = new Date()): string => {
  const d = parseUtcInstant(iso);
  if (!d) {
    return '';
  }

  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) {
    return 'Just now';
  }
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return `${diffMin} min ago`;
  }
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) {
    return `${diffHours} hr ago`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();
  if (isYesterday) {
    return 'Yesterday';
  }

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 7) {
    return d.toLocaleDateString([], { weekday: 'long' });
  }

  return d.toLocaleDateString();
};

/** Compact "h m s" duration. */
export const formatDurationShort = (seconds: number | null): string => {
  if (seconds == null || seconds < 0) {
    return '—';
  }
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) {
    return `${m}m ${s.toString().padStart(2, '0')}s`;
  }
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return `${h}h ${remM.toString().padStart(2, '0')}m`;
};

export interface IGroupedCall {
  /** The most-recent call to this number (used for tap / recall / drawer). */
  representative: ICallScribeCallListItem;
  /** Every call to this number, newest first. */
  members: ICallScribeCallListItem[];
  /** Number of members. ≥1 — when 1 the card hides the ×N badge. */
  count: number;
}

/**
 * One entry per dialled number across the WHOLE list: the newest call to a
 * number is its representative, every call to that number is a member, and
 * entries appear in the order of each number's newest call. Input must already
 * be sorted (BE returns newest-first by `createdAtUtc`).
 *
 * A call whose other party has no usable number gets a key of its own, so
 * unrelated numberless rows never merge into one entry.
 */
export const groupCallsByNumber = (
  calls: readonly ICallScribeCallListItem[],
): IGroupedCall[] => {
  const byKey = new Map<string, IGroupedCall>();
  const order: string[] = [];

  calls.forEach((call, index) => {
    const phone = normalizePhoneKey(otherPartyPhone(call));
    const key = phone === '' ? `\u0000unkeyed-${index}` : phone;
    const existing = byKey.get(key);

    if (existing) {
      existing.members.push(call);
      existing.count += 1;
      return;
    }
    const group: IGroupedCall = { representative: call, members: [call], count: 1 };
    byKey.set(key, group);
    order.push(key);
  });

  return order.map(key => byKey.get(key) as IGroupedCall);
};

/**
 * iPhone-style T9 keypad letter map. Keys 2-9 cover A-Z; 0 / 1 / + / * / # have
 * no letter mapping and only match digit substrings.
 */
const T9_LETTERS: Record<string, string> = {
  2: 'abc',
  3: 'def',
  4: 'ghi',
  5: 'jkl',
  6: 'mno',
  7: 'pqrs',
  8: 'tuv',
  9: 'wxyz',
};

/** Returns the digit a letter maps to, or null when the char is non-alphabetic. */
const letterToDigit = (ch: string): string | null => {
  const lower = ch.toLowerCase();
  for (const key of Object.keys(T9_LETTERS)) {
    if (T9_LETTERS[key].includes(lower)) {
      return key;
    }
  }
  return null;
};

/** Maps a name to its T9 digit string ("Smith" → "76484"). Non-letters are dropped. */
const nameToT9 = (name: string): string => {
  let out = '';
  for (const ch of name) {
    const d = letterToDigit(ch);
    if (d) {
      out += d;
    }
  }
  return out;
};

/**
 * iPhone-style T9 match: a query of digits matches a candidate when EITHER the
 * candidate's phone digits start with the query OR any name token's T9 mapping
 * starts with it. So "27" matches "Anna" (T9 = "2662") because "Anna" starts with
 * the digits matching A/B/C and P/Q/R/S — the same affordance iOS uses.
 */
export const t9MatchesQuery = (
  query: string,
  name: string | null | undefined,
  phone: string | null | undefined,
): boolean => {
  const digits = query.replace(/\D+/g, '');
  if (!digits) {
    return true;
  }
  const phoneKey = normalizePhoneKey(phone);
  if (phoneKey.includes(digits)) {
    return true;
  }
  if (!name) {
    return false;
  }
  // Match against the full T9 of the trimmed name and each whitespace-separated
  // token's T9 — typing "27" should match "Anna Smith" via the leading "A" and
  // also match a contact named "Bob Smith" via the "S" in "Smith".
  for (const token of name.split(/\s+/).filter(Boolean)) {
    if (nameToT9(token).startsWith(digits)) {
      return true;
    }
  }
  return nameToT9(name).startsWith(digits);
};
